import type { ConnectorBody } from './api'
import { t, type StaffKey, type StaffLanguage } from './staffText'
import type { Connector, ConnectorCall } from './types'

/**
 * The pure logic behind the Connectors section: the methods in the order the
 * server keeps them, what is wrong with a connector as typed, and the words for
 * how a call ended. Every check mirrors internal/connector's Validate, so the form
 * says it first and in the reader's language; the server remains the authority,
 * and also checks the address against the installation's outbound guard, which
 * only it knows.
 */

/** Every method a connector may allow, in internal/connector.Canonical's order. */
export const CONNECTOR_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const
export type ConnectorMethod = (typeof CONNECTOR_METHODS)[number]

/**
 * The methods chosen, as a list in the canonical order, whatever order they were
 * ticked in. The first is what a call naming no method is made with, so the form
 * and the server must agree on which that is.
 */
export function methodsInOrder(chosen: Iterable<string>): ConnectorMethod[] {
  const set = new Set(chosen)
  return CONNECTOR_METHODS.filter((m) => set.has(m))
}

/** Ticks a method on or off, keeping the canonical order. */
export function toggleMethod(methods: readonly string[], method: ConnectorMethod): ConnectorMethod[] {
  const set = new Set(methods)
  if (set.has(method)) set.delete(method)
  else set.add(method)
  return methodsInOrder(set)
}

/** Every outcome the call log keeps (internal/connector's Outcome.Logged). */
export const CALL_OUTCOMES = ['ok', 'http_error', 'timeout', 'refused_address', 'unreachable', 'too_large', 'cancelled'] as const
export type CallOutcome = (typeof CALL_OUTCOMES)[number]

export const OUTCOME_LABEL: Record<CallOutcome, StaffKey> = {
  ok: 'connectors.outcome.ok',
  http_error: 'connectors.outcome.http_error',
  timeout: 'connectors.outcome.timeout',
  refused_address: 'connectors.outcome.refused_address',
  unreachable: 'connectors.outcome.unreachable',
  too_large: 'connectors.outcome.too_large',
  cancelled: 'connectors.outcome.cancelled',
}

/** An outcome in words; one this version does not know is shown as the server
 *  wrote it rather than hidden. */
export function outcomeWords(outcome: string, lang: StaffLanguage): string {
  return outcome in OUTCOME_LABEL ? t(OUTCOME_LABEL[outcome as CallOutcome], lang) : outcome
}

/** The machine that made a call, as "BRANCH · UNIT", or a sentence once it is gone. */
export function callMachine(call: Pick<ConnectorCall, 'branchCode' | 'unitCode'>, lang: StaffLanguage): string {
  if (!call.unitCode) return t('connectors.machineGone', lang)
  return call.branchCode ? `${call.branchCode} · ${call.unitCode}` : call.unitCode
}

export interface ConnectorDraft extends Omit<ConnectorBody, 'paths'> {
  /** The allowed paths as typed, one to a line. */
  pathsText: string
  /** Only on a new connector; an existing one's secret is set on its own. */
  secret: string
}

/** The allowed paths typed one to a line, without blank lines or surrounding space. */
export function pathsOf(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '')
}

/** A new connector starts with GET and POST, the header most APIs read a key from,
 *  and five seconds. */
export function emptyConnectorDraft(): ConnectorDraft {
  return {
    name: '',
    url: '',
    methods: ['GET', 'POST'],
    pathsText: '',
    headerName: 'Authorization',
    timeoutMs: 5000,
    active: true,
    secret: '',
  }
}

export function connectorDraftOf(c: Connector): ConnectorDraft {
  return {
    name: c.name,
    url: c.url,
    methods: methodsInOrder(c.methods),
    pathsText: (c.paths ?? []).join('\n'),
    headerName: c.headerName,
    timeoutMs: c.timeoutMs,
    active: c.active,
    secret: '',
  }
}

/** The body the server is sent for a draft: trimmed, with the methods in order,
 *  and the secret only when there is one. */
export function connectorBodyOf(d: ConnectorDraft): ConnectorBody & { secret?: string } {
  return {
    name: d.name.trim(),
    url: d.url.trim(),
    methods: methodsInOrder(d.methods),
    paths: pathsOf(d.pathsText),
    headerName: d.headerName.trim(),
    timeoutMs: d.timeoutMs,
    active: d.active,
    ...(d.secret !== '' && { secret: d.secret }),
  }
}

/** internal/connector.MinSecretLen: a secret is empty (none) or at least this long. */
export const MIN_SECRET = 8

export function secretTooShort(secret: string): boolean {
  return secret !== '' && secret.length < MIN_SECRET
}

/** internal/connector's limits. */
export const NAME_PATTERN = /^[a-z0-9][a-z0-9-]{0,39}$/
export const MAX_URL_BYTES = 2048
export const MIN_TIMEOUT_MS = 500
export const MAX_TIMEOUT_MS = 10000
const HEADER_TOKEN = /^[A-Za-z0-9!#$%&'*+\-.^_`|~]{1,64}$/
const RESERVED_HEADERS = [
  'host',
  'content-length',
  'cookie',
  'connection',
  'content-type',
  'transfer-encoding',
  'user-agent',
  'te',
  'trailer',
  'upgrade',
  'keep-alive',
  'proxy-connection',
  'expect',
]

/** internal/connector's safeSegment: what one part of an allowed path may be. */
const SAFE_SEGMENT = /^[A-Za-z0-9._~:@+,=-]+$/
export const MAX_PATHS = 50
const MAX_PATTERN = 200

/**
 * Whether an allowed path is one the server accepts: an exact path, or a prefix
 * ending in /*, made of safe parts between single slashes; never /* alone.
 */
export function pathPatternValid(pattern: string): boolean {
  if (pattern === '/*' || pattern.length > MAX_PATTERN) return false
  const body = pattern.endsWith('/*') ? pattern.slice(0, -2) : pattern
  if (!body.startsWith('/')) return false
  return body
    .slice(1)
    .split('/')
    .every((seg) => SAFE_SEGMENT.test(seg) && seg !== '.' && seg !== '..')
}

function parsed(url: string): URL | null {
  try {
    return new URL(url.trim())
  } catch {
    return null
  }
}

export function connectorProblems(d: ConnectorDraft, lang: StaffLanguage): string[] {
  const out: string[] = []
  if (!NAME_PATTERN.test(d.name.trim())) out.push(t('connectors.problem.name', lang))
  const u = parsed(d.url)
  if (!u || (u.protocol !== 'https:' && u.protocol !== 'http:') || !u.hostname) {
    out.push(t('connectors.problem.url', lang))
  } else if (new TextEncoder().encode(d.url.trim()).length > MAX_URL_BYTES) {
    out.push(t('connectors.problem.urlLong', lang))
  } else if (u.username || u.password) {
    out.push(t('connectors.problem.password', lang))
  } else if (u.hash || d.url.trim().endsWith('#')) {
    out.push(t('connectors.problem.fragment', lang))
  }
  if (methodsInOrder(d.methods).length === 0) out.push(t('connectors.problem.methods', lang))
  const header = d.headerName.trim()
  if (header !== '' && !HEADER_TOKEN.test(header)) out.push(t('connectors.problem.header', lang))
  else if (RESERVED_HEADERS.includes(header.toLowerCase())) out.push(t('connectors.problem.headerReserved', lang))
  if (!Number.isInteger(d.timeoutMs) || d.timeoutMs < MIN_TIMEOUT_MS || d.timeoutMs > MAX_TIMEOUT_MS) {
    out.push(t('connectors.problem.timeout', lang))
  }
  const paths = pathsOf(d.pathsText)
  const badPaths = paths.filter((p) => !pathPatternValid(p))
  if (badPaths.length > 0) out.push(t('connectors.problem.paths', lang, { paths: badPaths.join(', ') }))
  if (paths.length > MAX_PATHS) out.push(t('connectors.problem.pathsMany', lang))
  // A line break in a secret could never be sent in a header; the server refuses
  // it too, and saying so here saves a round trip.
  if (/[\u0000-\u001f\u007f]/.test(d.secret)) out.push(t('connectors.problem.secret', lang))
  if (secretTooShort(d.secret)) out.push(t('connectors.problem.secretShort', lang))
  return out
}
